import type * as React from 'react'
import {
  getPreferredEditor,
  getPreviewForExtension,
  type EditorFamily,
  type EditorRegistration,
  type ViewerComponentProps,
} from './editor-registry.js'
import { chooseEditor, extensionOf, type EditorChoiceOptions, type EditorProbe } from './editor-choice.js'
import type { DocsApiDocumentType } from './editors/docsapi-document-types.js'
import { LARGE_CSV_ROUTING_ENABLED, isCsvOrTsvName } from '@papan/types'

// ─── The share view's editor choice (I111) ──────────────────────────
//
// The share view (apps/web ShareViewerChrome) used to keep its OWN switch over
// hard-coded extension sets, so it opened fewer types than the drive and sent
// everything it didn't name (audio, slides, tsv, rtf, pmeta…) to an iframe of the
// raw file. It now takes the ONE choice every surface takes — `chooseEditor`
// (#942): the registered editor, else txt for a non-binary file, and over the
// live editor's size limit the HTML preview (last saved page) or plain text —
//
// and then applies the share view's constraints as explicit CAPS on what the
// registry chose, keyed on the registration's FAMILY — never on extensions. A
// new extension added to a registration reaches the share view with nothing else
// to edit; a new family fails to compile here until someone decides its cap.
//
// There is no per-visitor `preferredEditors` setting in the share view (the
// visitor may be anonymous and has no workspace settings), so the preferred id is
// always unset — i.e. the registry's default for the extension, which is exactly
// what the drive picks for a user who never chose an alternative.

/** What the share view learned about the bytes, when the choice needs it. */
/** The byte probe, plus (#1518) whether the serve refused the file on a connection's link for holding a keybook
 *  value (`held`) or for a container it can't check (`uncheckable`) — then the view says so instead of framing the
 *  refusal body. */
export type ShareFileProbe = EditorProbe & { heldSecret?: 'held' | 'uncheckable' }

export type ShareEditorChoice =
  /** The choice depends on the bytes (unregistered extension → binary sniff, or a
   *  registration with `maxEditableSize`). Run the probe and choose again. */
  | { kind: 'probe' }
  /** The Y.Doc text editor (MarkdownEditor) — co-edits the owner's live room on an
   *  edit link, read-only otherwise. Covers md/txt/json/pmeta and the txt fallback. */
  | { kind: 'text'; registration: EditorRegistration }
  /** HtmlEditor (source + the running page). */
  | { kind: 'html-editor'; registration: EditorRegistration }
  /** HtmlEditor in preview-only: over the live editor's size limit — the page as
   *  last saved, no live room, nothing to save (#942). */
  | { kind: 'html-preview-only'; registration: EditorRegistration; inlineTooLarge?: boolean }
  /** ScriptEditor, CAPPED: Run is hidden (the visitor must not execute the owner's
   *  code in the owner's sandbox). The source still co-edits on an edit link. */
  | { kind: 'script'; registration: EditorRegistration }
  /** The drive's DocsAPI office editor in share mode (Save / Discard on an edit link). */
  | { kind: 'office-docsapi'; registration: EditorRegistration; documentType: DocsApiDocumentType; readOnly?: boolean }
  /** The drive's Collabora editor — CAPPED read-only: the share-session WOPI token
   *  is minted read-only by the server, whatever the link grants. */
  | { kind: 'office-collabora'; registration: EditorRegistration }
  /** The drive's SQLite editor — CAPPED read-only: /api/sqlite refuses every
   *  mutation from a share-kind principal. */
  | { kind: 'sqlite-readonly'; registration: EditorRegistration }
  /** The registry's read-only PREVIEW viewer for this extension, mounted because the
   *  drive's editor can't run here (see `capForShare`). */
  | { kind: 'preview'; viewer: React.ComponentType<ViewerComponentProps>; previewName: string; registration: EditorRegistration }
  /** Plain read-only text (ReadOnlyText). */
  | { kind: 'text-viewer'; reason: 'registered' | 'too-large' | 'connection-link' }
  /** Too large even for read-only text: nothing of it is loaded (the chrome's
   *  Download is the way to it). */
  | { kind: 'too-large' }
  /** A csv/tsv over LARGE_CSV_THRESHOLD_BYTES: the windowed large-CSV grid, read-only (#981). */
  | { kind: 'large-csv' }
  /** Nothing in the registry can render it here — the raw-file fallback. */
  | { kind: 'raw'; reason: 'folder' | 'unregistered-binary' | 'firebird' | 'no-preview' | 'app' }

/** The kinds that own Save / Discard (the editor wires onSaveRef / onDiscardRef). */
export type ShareSaveableKind = 'text' | 'html-editor' | 'script' | 'office-docsapi'

/** Whether a choice mounts an editor that can SAVE on an edit link — the chrome's
 *  Save/Discard gate. Derived from the same choice the body mounts, so the two
 *  can't disagree. */
export function shareChoiceSaveableKind(choice: ShareEditorChoice): ShareSaveableKind | null {
  switch (choice.kind) {
    case 'text':
    case 'html-editor':
    case 'script':
      return choice.kind
    case 'office-docsapi':
      // #1518 D10: a connection link's office editor is view-only — no Save.
      return choice.readOnly === true ? null : choice.kind
    default:
      return null
  }
}

/** The family of the editor the registry gives this file name (null = unregistered).
 *  For a surface that needs to know WHAT KIND of file it shows (e.g. "an HTML page")
 *  without keeping its own extension list. */
export function editorFamilyForName(name: string): EditorFamily | null {
  return getPreferredEditor(extensionOf(name))?.family ?? null
}

/**
 * Choose what the share view mounts for a file. Pure: the same inputs always give
 * the same answer, so the chrome's Save gate and the body's dispatch call it
 * independently and still agree.
 */
export function chooseShareEditor(
  file: { name: string; isFolder?: boolean },
  probe?: ShareFileProbe | null,
  /** `largeCsvRouting` (tests) and `hasFragmentEdits` apply: a share view mounts the large grid read-only, so
   *  unsaved whole-file edits in the owner's room don't send it back to the old editor — but unsaved LARGE-GRID
   *  edits keep a csv/tsv in the grid whatever its size, as on the drive (#981 QA 14). */
  opts?: Pick<EditorChoiceOptions, 'largeCsvRouting' | 'hasFragmentEdits'> & {
    /** #1518 D10: the link was minted by an MCP connection — no live room; text opens read-only over
     *  /s/ (masked), the office editor opens view-only. */
    connectionLink?: boolean
  },
): ShareEditorChoice {
  // The share view never opens a folder as an editor (an app runs separately).
  if (file.isFolder) return { kind: 'raw', reason: 'folder' }
  // No preferred editor: a visitor may be anonymous and has no settings.
  const choice = chooseEditor({ name: file.name }, probe, opts)
  switch (choice.kind) {
    case 'probe':
      return { kind: 'probe' }
    case 'large-csv':
      return { kind: 'large-csv' }
    case 'unsupported':
      return { kind: 'raw', reason: 'unregistered-binary' }
    case 'text-viewer':
      // Over the limit, a type with no preview-only mode: the bytes are the
      // visitor's to read.
      return { kind: 'text-viewer', reason: 'too-large' }
    case 'too-large':
      return { kind: 'too-large' }
    case 'editor':
      if (choice.previewOnly) {
        return choice.inlineTooLarge
          ? { kind: 'html-preview-only', registration: choice.registration, inlineTooLarge: true }
          : { kind: 'html-preview-only', registration: choice.registration }
      }
      return capForShare(choice.registration, extensionOf(file.name), opts?.connectionLink === true)
  }
}

/**
 * #981 QA 14: must the share view ask `legacy-edits` before mounting this choice? A csv/tsv the size rule sends to the
 * read-only CSV preview may hold unsaved large-grid edits, which only the large grid shows — the same rule as the
 * drive's EditorHost. (A visitor never edits a csv through a link, so only `hasFragmentEdits` matters here.)
 */
export function shareNeedsFragmentEditsAnswer(
  file: { name: string; isFolder?: boolean },
  sizeChoice: ShareEditorChoice,
  opts?: Pick<EditorChoiceOptions, 'largeCsvRouting'>,
): boolean {
  if (file.isFolder || sizeChoice.kind !== 'preview' || sizeChoice.registration.family !== 'spreadsheet') return false
  return (opts?.largeCsvRouting ?? LARGE_CSV_ROUTING_ENABLED) && isCsvOrTsvName(file.name)
}

/**
 * #1518: on a connection's link the serve refuses a file that holds a keybook value (403) or a container it can't
 * check (409) — and the raw fallback would frame that refusal's JSON. So EVERY raw choice there (an unregistered
 * binary, Firebird, a type with no preview) asks the byte probe first, which names the refusal
 * (`ShareFileProbe.heldSecret`) for the view to say in words. A folder is never framed; an ordinary link never
 * meets this refusal.
 */
export function shareRawNeedsProbe(choice: ShareEditorChoice, connectionLink: boolean): boolean {
  return connectionLink && choice.kind === 'raw' && choice.reason !== 'folder'
}

/** The share view's caps, one per editor family. Exhaustive by type. */
function capForShare(registration: EditorRegistration, ext: string, connectionLink = false): ShareEditorChoice {
  const preview = (): ShareEditorChoice => {
    const p = getPreviewForExtension(ext)
    // Every preview registered for these families is FileIO-backed (Image / Video /
    // Audio / PDF / CSV / Parquet) and treats a missing versionId as "the current
    // head" — which is what lets it read through the share FileIO.
    return p
      ? { kind: 'preview', viewer: p.component as unknown as React.ComponentType<ViewerComponentProps>, previewName: p.name, registration }
      : { kind: 'raw', reason: 'no-preview' }
  }
  if (connectionLink && (registration.family === 'text' || registration.family === 'html-page' || registration.family === 'script')) {
    // #1518 D10: no room for a connection link — ReadOnlyText reads through shareFileIO (/s/, masked).
    return { kind: 'text-viewer', reason: 'connection-link' }
  }
  switch (registration.family) {
    case 'text':
      // The live collab room is the share view's write path (shareFileIO has no
      // save endpoint), and MarkdownEditor saves through it for EVERY extension it
      // handles — txt / json / pmeta exactly as md. So an edit link co-edits these too.
      return { kind: 'text', registration }
    case 'html-page':
      return { kind: 'html-editor', registration }
    case 'script':
      return { kind: 'script', registration }
    case 'office-docsapi': {
      const documentType = registration.defaultProps?.documentType as DocsApiDocumentType | undefined
      return documentType
        ? { kind: 'office-docsapi', registration, documentType, ...(connectionLink ? { readOnly: true } : {}) }
        : { kind: 'raw', reason: 'no-preview' }
    }
    case 'office-collabora':
      return { kind: 'office-collabora', registration }
    case 'sqlite':
      return { kind: 'sqlite-readonly', registration }
    case 'spreadsheet':
      // CAPPED to the registry's read-only CSV preview. SpreadsheetEditor's Edit
      // mode (Univer) dispatches its add-sheet flow to the drive's dialog, which
      // the share view doesn't mount, so editing through a link is NOT something
      // it already does here — a csv/tsv opens read-only on every link. (Its
      // `readOnly` mode, #942, is the drive's: View only, for a Read-only viewer.)
      return preview()
    case 'media':
      // The drive's MediaEditor is the bare viewer plus a drive toolbar (Share
      // menu etc.); the share view has its own chrome, so it mounts the bare
      // viewer the registry previews this extension with.
      return preview()
    case 'datagrid':
      return preview()
    case 'firebird':
      // /api/fdb has no share-session auth (and needs the database's own
      // credentials), so the editor would only ever show errors here.
      return { kind: 'raw', reason: 'firebird' }
    case 'text-viewer':
      return { kind: 'text-viewer', reason: 'registered' }
    case 'app':
      return { kind: 'raw', reason: 'app' }
    default: {
      const unhandled: never = registration.family
      throw new Error(`chooseShareEditor: no share-view cap for editor family ${String(unhandled)}`)
    }
  }
}
